<template>
  <div class="infocenter">
    <v-toolbar class="custom-toolbar mb-4" flat dense>
      <img :src="logo" class="toolbar-icon" />
      <v-toolbar-title class="custom-toolbar-title">
        {{ $t('news.info-center') }}
      </v-toolbar-title>
      <v-btn icon style="right: 0;position:fixed" @click="refresh">
        <v-icon @click="refresh" color="#007BFF">mdi-refresh</v-icon>
      </v-btn>
    </v-toolbar>
    <Loader :loading="showLoader" />

    <div v-if="!isMobileView">
      <InfoCenterDesktop v-if="!showLoader" :newsItems="newsItems" :weather="currentWeather"
        :upcomingEvents="upcomingEvents" :userProfile="userOwnProfile" :canEditCreate="canCreateEdit"
        :rootProfile="rootProfile" :itemClicked="openDialog" :editItem="editItem" :deleteItem="deleteItem"
        :isEditModeParent="isEditMode" @update:isEditModeParent="updateEditMode" />
    </div>
    <div v-else>
      <InfoCenterMobile v-if="!showLoader" :newsItems="newsItems" :weather="currentWeather"
        :upcomingEvents="upcomingEvents" :userProfile="userOwnProfile" :canEditCreate="canCreateEdit"
        :rootProfile="rootProfile" :itemClicked="openDialog" :editItem="editItem" :deleteItem="deleteItem"
        :isEditModeParent="isEditMode" @update:isEditModeParent="updateEditMode" />
    </div>
    <!-- Dialog Box for Detailed View -->
    <v-dialog v-model="showDialog" max-width="800px">
      <v-card class="elegant-dialog-card">
        <!-- Dialog Toolbar -->
        <v-toolbar flat class="dialog-toolbar">
          <v-chip variant="elevated" class="ml-5" color="black" density="comfortable" pill>
            {{ selectedItem?.category }}
          </v-chip>
          <v-spacer></v-spacer>
          <v-btn icon @click="showDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text>
          <!-- Main Container with Grid Layout -->
          <v-container fluid>
            <!-- Category Section -->
            <v-row class="mb-2">
              <v-col cols="12">
                <header>
                  <h1>{{ selectedItem?.title }}</h1>
                </header>
              </v-col>
            </v-row>

            <!-- Title Image Section -->
            <v-row v-if="selectedItem?.image" class="mb-3 justify-center">
              <v-col cols="12" lg="6" md="5" sm="6">
                <v-card flat class="title-image-card">
                  <v-img :src="selectedItem.image" class="title-image" max-width="600px" />
                </v-card>
              </v-col>
            </v-row>

            <!-- Short Text Description Section -->
            <v-row class="mb-3">
              <v-col cols="12">
                <v-card flat class="description-card">
                  <div v-html="selectedItem?.text" class="item-text"></div>
                </v-card>
              </v-col>
            </v-row>

            <!-- Additional Images Section -->
            <v-row v-if="selectedItem?.additionalImages && selectedItem.additionalImages.length > 0" class="mb-4">
              <v-col cols="12">
                <v-card flat class="additional-images-card">
                  <v-card-title> {{ $t('news.additional-images') }} </v-card-title>
                  <v-row class="images-grid" dense>
                    <v-col v-for="(image, index) in selectedItem.additionalImages" :key="index" cols="6" sm="4" md="3">
                      <div class="additional-image-container" @click="openImageModal(image)"
                        @touchstart="showHoverEffect(index)" @touchend="hideHoverEffect(index)">
                        <v-img :src="image" class="additional-image" />
                        <div :class="['additional-image-hover-text', { visible: activeIndex === index }]">
                          <v-icon left>mdi-eye</v-icon>
                          {{ $t('news.view-image') }}
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>

            <!-- Files Section -->
            <v-row v-if="selectedItem?.files && selectedItem.files.length > 0" class="mb-3">
              <v-col cols="12">
                <v-card flat class="files-card">
                  <v-card-title>{{ $t('news.files') }}</v-card-title>
                  <v-row dense>
                    <v-col v-for="(file, index) in selectedItem.files" :key="index" cols="12" sm="6" md="4">
                      <v-card class="mx-auto" prepend-icon="mdi-file" variant="elevated" elevation="5">
                        <template v-slot:title>
                          <span class="font-weight-black" style="font-size: 15px; white-space: break-spaces">{{
                            file.fileName }}</span>
                        </template>

                        <v-card-actions class="light pt-4" style="background: aliceblue">
                          <v-btn class="elegant-button" @click="viewFile(file)">
                            <v-icon left>mdi-eye</v-icon>
                            {{ $t('news.view') }}
                          </v-btn>
                          <v-btn class="elegant-button" @click="downloadFile(file)">
                            <v-icon left>mdi-download</v-icon>
                            {{ $t('news.download') }}
                          </v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>

            <!-- PDF Viewer Modal -->
            <v-row v-if="pdfDialog" class="mb-4">
              <v-col cols="12">
                <v-card flat class="pdf-viewer-card">
                  <PDF v-if="pdfSrc" :src="pdfSrc" />
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
      <div v-if="imageModalOpen" class="custom-image-modal">
        <div class="custom-image-modal-content">
          <button class="close-button" @click="closeImageModal">×</button>
          <img :src="currentImageSrc" alt="Enlarged Image" class="modal-image" />
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted } from 'vue'
import { getToolbarLogoImage, getCustomer } from '@/assets/Branding/branding'
import user_avatar from '@/assets/General/user_avatar.png'
import Loader from '@/components/general/Loader.vue'
import defaultInfoItem from '@/assets/Branding/Pia/defaultInfoItem.png'
import { getEvents, getNews, createGetImageByContext } from '@/services/HBC/GeminiHelper'
import {
  getInfoCategories,
  getInfoItems,
  getFile,
  getDataFromBlob,
  getUserProfiles,
  getFileInfo,
  isStaingOrQa,
  deleteInfoItem,
  getFeatures,
  getUserProfileById,
} from '@/controllers/BaseController'
import { removeStoredItem } from '@/services/utils'
import { storeBlob } from '@/services/utils'
import { getUsers } from '@/controllers/BackboneAPI'
import moment from 'moment'
import Swal from 'sweetalert2'
import { useRouter } from 'vue-router'
import InfoCenterDesktop from '@/components/general/ChildComponents/InfoCenterDesktop.vue'
import InfoCenterMobile from '@/components/general/ChildComponents/InfoCenterMobile.vue'
import lodash from 'lodash'
import PDF from 'pdf-vue3'
import { useOAuthService } from '@/services/OAuthService'

export default defineComponent({
  components: {
    Loader,
    InfoCenterDesktop,
    InfoCenterMobile,
    PDF,
  },
  setup() {
    const logo = ref('')
    const router = useRouter()
    const OAuthService = useOAuthService()
    let userOwnProfile = ref({})
    const isMobileView = ref(window.innerWidth <= 960)
    const allInfoItems = ref([])
    const isEditMode = ref(false)
    const canCreateEdit = ref(false)
    const currentWeather = ref({})
    const teamMembers = ref([])
    const upcomingEvents = ref([])
    const newsItems = ref([])
    const showLoader = ref(false)
    const rootProfile = ref({})
    const showDialog = ref(false)
    const selectedItem = ref(null)
    const pdfDialog = ref(false)
    const pdfSrc = ref('')
    const infoCategories = ref([])
    const imageModalOpen = ref(false)
    const currentImageSrc = ref('')
    const activeIndex = ref(null)
    const showTeamMembers = ref(false)

    const handleResize = () => {
      isMobileView.value = window.innerWidth <= 960
    }

    const editItem = item => {
      item = JSON.parse(JSON.stringify(item))
      if (!item?.id) {
        let INFOITEMS = JSON.parse(JSON.stringify(allInfoItems.value))
        item = lodash.find(INFOITEMS, { title: item.title })
      }
      router.push({ name: 'EditInfoItem', params: { id: item.id } }).catch(err => console.error(err))
    }

    const deleteItem = async item => {
      try {
        item = JSON.parse(JSON.stringify(item))
        console.log('Deleting item:', item)
        if (!item?.id) {
          let INFOITEMS = JSON.parse(JSON.stringify(allInfoItems.value))
          item = lodash.find(INFOITEMS, { title: item.title })
        }

        const result = await Swal.fire({
          title: 'Are you sure?',
          text: 'Do you really want to delete this item? This action cannot be undone.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, delete it!',
          confirmButtonColor: '#90EE90',
          cancelButtonColor: '#FF7F7F',
          cancelButtonText: 'Cancel',
          reverseButtons: true,
        })

        if (result.isConfirmed) {
          Swal.fire({
            title: 'Deleting...',
            text: 'Please wait while we delete the item.',
            icon: 'info',
            allowOutsideClick: false,
            showConfirmButton: false,
            didOpen: () => {
              Swal.showLoading()
            },
          })

          showLoader.value = true
          await deleteInfoItem(item.id)

          Swal.fire({
            title: 'Deleted!',
            text: 'The item has been deleted successfully.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false,
          }).then(async () => {
            let countdown = 3
            const countdownSwal = Swal.fire({
              title: 'Refreshing in 3 seconds...',
              text: 'Refreshing in 3 seconds...',
              icon: 'info',
              timer: 3000,
              allowOutsideClick: false,
              showConfirmButton: false,
              willOpen: () => {
                const interval = setInterval(() => {
                  countdown--
                  Swal.update({
                    text: `Refreshing in ${countdown} seconds...`,
                  })
                  if (countdown === 0) clearInterval(interval)
                }, 1000)
              },
            })

            await countdownSwal
            await removeStoredItem('infoItems')
            await removeStoredItem('infoCategories')
            await removeStoredItem('factoryNews')
            refreshPage()
          })
        }
      } catch (error) {
        console.log(error)
        Swal.fire({
          title: 'Failed!',
          text: 'Failed to delete the item. Please try again.',
          icon: 'error',
          timer: 2000,
          showConfirmButton: false,
        })
      } finally {
        showLoader.value = false
      }
    }

    const refreshPage = () => {
      router.go(0)
    }

    window.addEventListener('resize', handleResize)

    const refresh = async () => {
      try {
        showLoader.value = true
        await removeStoredItem('infoItems_de')
        await removeStoredItem('infoItems_en')
        await removeStoredItem('infoCategories')
        await removeStoredItem('factoryNews')
        await removeStoredItem('factoryEvents')
      } catch (error) {
        console.error(error)
      } finally {
        location.reload()
      }
    }

    onMounted(async () => {
      showLoader.value = true

      try {
        await fetchWeather()

        // Run all independent setup API calls in parallel
        let [
          CUSTOMER,
          features,
          profiles,
          profile,
          logoImage,
          infoItems,
          infoCategories,
          isPIA
        ] = await Promise.all([
          getCustomer(),
          getFeatures(),
          getUserProfiles(),
          OAuthService.getUserProfile(),
          getToolbarLogoImage(),
          getInfoItems(),
          getInfoCategories(),
          isStaingOrQa()
        ])
        console.log(infoItems)

        rootProfile.value = profile
        logo.value = logoImage
        canCreateEdit.value = ['globaladmin', 'admin'].some(role =>
          profile?.realm_access?.roles?.includes(role)
        )

        if (features?.communcation?.teamMembers) showTeamMembers.value = true

        userOwnProfile.value = profiles.find(p => p.email === profile.email) || {}

        const apiProfile = await getUserProfileById(userOwnProfile.value.id || '') || {}
        userOwnProfile.value.jobTitle = apiProfile.job || 'Employee'

        const imageId = lodash.get(apiProfile, 'files.entities', []).find(f => f.type === 'profileImage')?.id
        const existingImg = apiProfile.profileImage

        if (imageId && !existingImg) {
          try {
            const file = await getFile(imageId)
            userOwnProfile.value.profileImage = apiProfile.profileImage = file
              ? await getDataFromBlob(file)
              : user_avatar
            await storeBlob(`userProfile_${apiProfile.id}`, apiProfile)
          } catch {
            userOwnProfile.value.profileImage = user_avatar
          }
        } else {
          userOwnProfile.value.profileImage = existingImg || user_avatar
        }

        // Categorize info items
        const filterByCategory = (names) =>
          names.flatMap(name =>
            infoCategories
              .filter(cat => cat?.name?.toLowerCase() === name)
              .flatMap(cat => infoItems.filter(item => item.infoCategory.id === cat.id))
          )

        const newsAnnounceItems = filterByCategory(['news', 'announcement'])
        const eventItems = filterByCategory(['events'])
        // Handle image loading for news items in parallel
        await Promise.all(
          newsAnnounceItems.map(async item => {
            try {
              const titleImageId = lodash.get(item, 'files.entities', []).find(f => f.type === 'titleimage')?.id
              const text = item?.text || item?.shortText || ''
              const fallbackText = item?.shortText || item?.text || ''

              item.image = titleImageId
                ? await getDataFromBlob(await getFile(titleImageId))
                : await createGetImageByContext(text) || defaultInfoItem

              item.description = fallbackText
            } catch (err) {
              console.error('Error processing news item:', err)
              item.image = defaultInfoItem
              item.description = item?.shortText || item?.text || ''
            }
          })
        )

        newsItems.value = newsAnnounceItems

        const now = moment()
        upcomingEvents.value = eventItems.map(event => {
          let dateStr = event?.shortText
          let parsedDate = moment(dateStr, ['MMMM D, YYYY', 'YYYY-MM-DD', moment.ISO_8601], true)

          if (isPIA && parsedDate.isValid() && parsedDate.isBefore(now)) {
            parsedDate = now.add(7, 'days')
            dateStr = parsedDate.format('MMMM D, YYYY')
          }

          return {
            title: event.title,
            id: event.id,
            description: event.text?.replace(/<\/?[^>]+(>|$)/g, '') || '',
            date: dateStr,
          }
        })

      } catch (err) {
        console.error('onMounted() error:', err)
      } finally {
        showLoader.value = false
        getUsers()
      }
    })



    async function fetchWeather() {
      const apiKey = '4ae793a3dd93a074ff16d7e97b06ec5b'
      const city = 'Munich,DE'
      const response = await fetch(`https://api.openweathermap.org/data/2.5/weather?q=${city}&appid=${apiKey}&units=metric`)
      const data = await response.json()
      currentWeather.value = {
        temp: data.main.temp.toFixed(1),
        desc: data.weather[0].description,
        humidity: data.main.humidity,
        wind: data.wind.speed,
        rain: data.rain ? data.rain['1h'] : 0
      }
    }

    const openDialog = async item => {
      try {
        showLoader.value
        let itemSelected = JSON.parse(JSON.stringify(item))
        let image = lodash.find(itemSelected.files?.entities, { type: 'titleimage' })
        let files = lodash.filter(itemSelected.files?.entities, { type: 'attachment' })
        let additionalImages = lodash.filter(itemSelected.files?.entities, { type: 'image' })

        if (itemSelected.dummy) {
          // Handle dummy news items
          itemSelected.image = image?.url || defaultInfoItem
          if (additionalImages?.length > 0) {
            itemSelected.additionalImages = additionalImages.map(img => img.url || 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e')
          }
          if (files?.length > 0) {
            itemSelected.files = files.map(file => ({
              id: file.id || Math.random().toString(36).substring(2, 15),
              fileName: `Document_${Math.random().toString(36).substring(2, 7)}.pdf`,
              url: 'https://via.placeholder.com/150' // Placeholder for dummy files
            }))
          }
        } else {
          // Handle real API items (original logic)
          if (additionalImages?.length > 0) {
            let imageIds = additionalImages.map(image => image.id)
            let detailedImages = []
            for (let imageId of imageIds) {
              let image = await getDataFromBlob(await getFile(imageId))
              detailedImages.push(image)
            }
            itemSelected.additionalImages = detailedImages
          }

          if (files?.length > 0) {
            let fileIds = files.map(file => file.id)
            let detailedFiles = []
            for (let fileId of fileIds) {
              let file = await getFileInfo(fileId)
              file.fileName = file?.fileName?.split('.')[0]
              file.url = await getFile(file.id)
              file.url = await getDataFromBlob(file.url)
              detailedFiles.push(file)
            }
            itemSelected.files = detailedFiles
          }

          itemSelected.image = image ? await getDataFromBlob(await getFile(image.id)) : defaultInfoItem
        }

        let category = infoCategories.value.find(category => category.id === itemSelected.infoCategory?.id)?.name || 'News'
        itemSelected.category = category

        selectedItem.value = itemSelected
        if (!isEditMode.value) {
          showDialog.value = true
        }
      } catch (error) {
        console.error('Error opening dialog:', error)
        showLoader.value = false
      }

    }

    const viewFile = file => {
      pdfDialog.value = false
      pdfSrc.value = null
      setTimeout(() => {
        pdfSrc.value = file.url
        pdfDialog.value = true
      }, 100)
    }

    const downloadFile = file => {
      file = JSON.parse(JSON.stringify(file))
      const link = document.createElement('a')
      link.href = file.url
      link.download = file.fileName.split('.').shift()
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    const openImageModal = image => {
      currentImageSrc.value = image
      imageModalOpen.value = true
    }

    const closeImageModal = () => {
      imageModalOpen.value = false
      currentImageSrc.value = ''
    }

    const showHoverEffect = index => {
      activeIndex.value = index
    }

    const hideHoverEffect = () => {
      activeIndex.value = null
    }

    const updateEditMode = newEditMode => {
      isEditMode.value = newEditMode
    }

    return {
      showLoader,
      logo,
      isMobileView,
      upcomingEvents,
      refresh,
      newsItems,
      rootProfile,
      userOwnProfile,
      showDialog,
      selectedItem,
      pdfDialog,
      router,
      pdfSrc,
      openDialog,
      viewFile,
      downloadFile,
      editItem,
      deleteItem,
      infoCategories,
      openImageModal,
      closeImageModal,
      imageModalOpen,
      currentImageSrc,
      showHoverEffect,
      hideHoverEffect,
      activeIndex,
      isEditMode,
      updateEditMode,
      allInfoItems,
      showTeamMembers,
      canCreateEdit,
      currentWeather,
      fetchWeather,
    }
  },
})
</script>

<style scoped>
/* Custom Toolbar Styles */
.custom-toolbar {
  background-color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  color: #333333;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 8px 16px;
}

@supports (-webkit-touch-callout: none) {
  .custom-toolbar {
    position: sticky;
    top: 0;
    z-index: 1000;
  }
}

.custom-toolbar-title {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 1.5rem;
  color: #333333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: calc(100% - 120px);
}

.toolbar-icon {
  height: 30px;
  align-self: center;
}

.infocenter {
  padding: 1rem;
}

/* Dialog Box Styles */
.elegant-dialog-card {
  background-color: rgba(255, 255, 255, 1.11);
  border-radius: 12px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.dialog-toolbar {
  background-color: #f5f5f5;
  border-bottom: 1px solid #ddd;
}

.category-chip {
  font-weight: bold;
  background-color: #007bff;
  color: white;
  padding: 8px;
  border-radius: 16px;
  font-size: 1rem;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
}

.title-image-card {
  border-radius: 8px;
  overflow: hidden;
  max-width: 100%;
}

.title-image {
  max-width: 100%;
  width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.description-card {
  background-color: rgba(255, 255, 255, 0.9);
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
}

.additional-images-card {
  padding: 16px;
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.9);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
}

.images-grid {
  gap: 16px;
  flex-wrap: wrap;
}

/* Additional Images Container with Hover Effect */
.additional-image-container {
  position: relative;
  overflow: hidden;
}

.additional-image {
  transition: 0.5s ease;
  display: block;
  width: 100%;
  height: auto;
  backface-visibility: hidden;
}

.additional-image-hover-text {
  transition: 0.5s ease;
  opacity: 0;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  -ms-transform: translate(-50%, -50%);
  text-align: center;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
}

/* Hover effect for desktop */
.additional-image-container:hover .additional-image {
  opacity: 0.3;
}

.additional-image-container:hover .additional-image-hover-text,
.additional-image-hover-text.visible {
  opacity: 1;
}

.custom-image-modal {
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  z-index: 1000;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
}

.custom-image-modal-content {
  position: relative;
  max-width: 80%;
  max-height: 80%;
}

.modal-image {
  width: 100%;
  height: auto;
  border-radius: 8px;
}

.close-button {
  position: absolute;
  top: 10px;
  right: 15px;
  background-color: white;
  border: none;
  font-size: 24px;
  cursor: pointer;
}

.files-card {
  padding: 16px;
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.9);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
}

.elegant-chip {
  padding: 10px;
  border-radius: 8px;
  background-color: #f5f5f5;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
  margin: 0.5rem;
}

.elegant-chip:hover {
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
  background-color: #e9e9e9;
}

.pdf-modal {
  width: 100%;
  max-height: 500px;
  overflow: auto;
  margin-top: 1rem;
  border: 1px solid #ddd;
  padding: 1rem;
  border-radius: 8px;
}

.pdf-viewer-card {
  padding: 16px;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

header h1 {
  font-size: 55px;
  font-weight: 600;
  background-image: linear-gradient(45deg, #553c9a, #ee4b2b);
  color: transparent;
  background-clip: text;
  -webkit-background-clip: text;
}

@media (max-width: 640px) {
  .item-text {
    font-size: small;
  }

  .custom-toolbar-title {
    font-size: large;
  }

  header h1 {
    font-size: 35px;
  }
}

@media (max-width: 400px) {
  .item-text {
    font-size: smaller;
  }

  header h1 {
    font-size: 25px;
  }
}
</style>

<!-- InfoCenterView Component
This component provides a comprehensive information center for users, offering a variety of functionalities and interactive elements. 

At the top, there is a toolbar with a logo, a title "Communication", and a refresh button. The refresh button allows users to reload the content of the information center.

Below the toolbar, a Loader component is displayed when data is being fetched or processed, providing visual feedback to users that an operation is in progress.

The main content of the InfoCenterView is divided into two sections: one for desktop view and one for mobile view. The component dynamically switches between these views based on the screen width.

In the desktop view, the InfoCenterDesktop component is used, while in the mobile view, the InfoCenterMobile component is used. Both components display news items, team members, upcoming events, and user profiles. Users can interact with these items by clicking on them to open a detailed view dialog.

The detailed view dialog is a modal that provides an in-depth look at a selected item. It includes a toolbar with the item's category and a close button. The dialog displays the item's title, an optional title image, a description, additional images, and files. Users can view or download files directly from the dialog. If the item includes a PDF file, it can be viewed within the dialog using the PDF viewer.

The component also includes functionality for editing and deleting items. When an item is edited, the user is redirected to an edit page. When an item is deleted, a confirmation dialog is shown using Swal. If the user confirms the deletion, a loading indicator is displayed, and the item is deleted. Upon successful deletion, a success message is shown, and the page is refreshed after a short countdown. If the deletion fails, an error message is displayed.

Users can also open an image modal to view additional images in a larger format. The image modal includes a close button to return to the previous view.

Overall, the InfoCenterView component provides a rich and interactive experience for users, allowing them to access and manage information efficiently. -->
